import * as _ from 'lodash-es';
import {
    AsyncSubject,
    EMPTY,
    iif,
    merge,
    NEVER,
    Observable,
    race,
    Subject,
    Subscriber,
    TeardownLogic,
    timer
} from 'rxjs';
import {filter, share, switchMap, take, takeUntil, tap} from 'rxjs/operators';
import {dependOn} from '../../common/rxjs/depend-on';
import {UPDATES_GATING_PERIOD} from '../layout/widget/widget-updates-gating';
import type {SubscriptionSubject} from './subscription-streaming.service';
import {SubscriptionSubjectType} from './subscription-subject-type';

export interface SubscriptionCommand {
    operation: string;
    requestId?: string;
    subject: SubscriptionSubject;
}

interface SubscriptionActualityMap {
    needed?: SubscriptionSubject;
    active?: SubscriptionSubject;
    subject?: SubscriptionSubject;
}

export class SubscriptionDescriptor<T = any> {

    private subjects: SubscriptionSubject[] = [];
    private actualityMap: SubscriptionActualityMap = {};

    private readonly stream$: Observable<any>;

    private readonly innerActualize$ = new Subject<void>();

    private readonly destroy$ = new AsyncSubject<any>();

    private readonly waitForReady$ = this.ready$.pipe(take(1));
    private readonly waitForNotReady$ = this.notReady$.pipe(take(1));

    private readonly handleDisconnections: Observable<any> = this.notReady$.pipe(
        tap(() => this.deactivateSubjects()),
        tap(() => {
            if (this.subjectType.disconnected$) {
                this.subjectType.disconnected$.next();
            }
        })
    );

    constructor(
        public subjectType: SubscriptionSubjectType<T>,

        private ready$: Observable<any>,
        private notReady$: Observable<any>,
        private actualize$: Observable<SubscriptionDescriptor>,
        private command$: Subject<SubscriptionCommand>,
        private message$: Observable<any>
    ) {
        this.stream$ = this.createStream();

        this.listenToActualizationRequests();
    }

    public destroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    public createSubscription(subject?: SubscriptionSubject): Observable<T> {
        const {subjectType} = this;

        return new Observable((subscriber: Subscriber<T>): TeardownLogic => {
            const innerSubject = subjectType.isMultiSubject
                ? subject && subject.slice()
                : [undefined]; // Not configurable subscription. The array is needed for proper subscriptions work.

            this.addSubject(innerSubject);

            const subscription = this.createSubjectStream(subject)
                .subscribe(subscriber);

            return () => {
                this.removeSubject(innerSubject);
                subscription.unsubscribe();
            };
        }).pipe(
            dependOn(this.handleDisconnections),
            takeUntil(this.destroy$)
        );
    }

    public resendSubscription(subjectType: SubscriptionSubjectType, subject: SubscriptionSubject): void {
        const isAlreadyActive = this.actualityMap.active
            && !_.difference(subject, this.actualityMap.active).length;

        if (isAlreadyActive) {
            this.command$.next({
                operation: subjectType.operations.SUBSCRIBE,
                requestId: subjectType.requestId,
                subject
            });
        }
    }

    private collectSubject(): void {
        this.actualityMap.subject = _.union(...this.subjects);
    }

    private listenToActualizationRequests(): void {
        const handleAdding$ = this.getAddingActualizationHandler();
        const handleRemoving$ = this.getRemovingActualizationHandler();

        const actualizationRequest$ = merge(
            this.actualize$,
            this.innerActualize$
        ).pipe(
            filter(descriptor => !descriptor || descriptor === this)
        );

        actualizationRequest$.pipe(
            switchMap(() => merge(
                handleRemoving$,
                handleAdding$
            )),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private getAddingActualizationHandler(): Observable<any> {
        const isSubjectNotEmpty = () => {
            this.collectSubject();

            return !!this.actualityMap.subject.length;
        };

        return iif(isSubjectNotEmpty, this.waitForReady$, EMPTY).pipe(
            tap(() => this.actualizeSubscription())
        );
    }

    private getRemovingActualizationHandler(): Observable<any> {
        return race(
            this.waitForNotReady$,
            timer(UPDATES_GATING_PERIOD),
            (this.subjectType.sharedActualizeUnsubscription$ || NEVER) as Observable<any>
        ).pipe(
            tap(() => this.actualizeUnsubscription())
        );
    }

    private actualizeSubscription(): void {
        const {subjectType, actualityMap} = this;

        this.collectSubject();

        // Find out the items that needed, but not active yet.
        const appended: SubscriptionSubject = _.difference(actualityMap.subject, actualityMap.active);

        console.log('[SubscriptionDescriptor]', 'Actualize new',
            this.subjectType,
            this,
            {
                origActualityMap: _.cloneDeep(this.actualityMap),
                appended
            }
        );

        if (appended.length) {
            const subject = subjectType.isMultiSubject ? appended : undefined;

            if (this.subjectType.sharedActualizeUnsubscription$) {
                this.subjectType.sharedActualizeUnsubscription$.next();
            }

            if (subjectType.splitSubjectOnSubscribe && subjectType.isMultiSubject) {
                subject.forEach(subjectPart =>
                    this.command$.next({
                        operation: subjectType.operations.SUBSCRIBE,
                        requestId: subjectType.requestId,
                        subject: [subjectPart]
                    })
                );
            } else {
                this.command$.next({
                    operation: subjectType.operations.SUBSCRIBE,
                    requestId: subjectType.requestId,
                    subject
                });
            }

            actualityMap.active = _.union(actualityMap.active, actualityMap.subject);
            actualityMap.needed = _.union(actualityMap.needed, appended);

            if (this.subjectType.subscriptionSent$) {
                this.subjectType.subscriptionSent$.next(subject);
            }
        }
    }

    private actualizeUnsubscription(): void {
        const {subjectType, actualityMap} = this;

        this.collectSubject();

        // Find out the items that were needed, but aren't now.
        const outdatedNeeded = _.difference(
            actualityMap.needed,
            actualityMap.subject
        );

        // Find out the items that were active, but are not needed now.
        const activeButNotNeeded: SubscriptionSubject = _.difference(actualityMap.active, actualityMap.subject);

        console.log(`[SubscriptionDescriptor]`, 'Actualize deprecated',
            subjectType,
            this,
            {
                origActualityMap: _.cloneDeep(actualityMap),
                activeButNotNeeded,
                outdatedNeeded
            }
        );

        if (outdatedNeeded.length) {
            actualityMap.needed = _.difference(actualityMap.needed, outdatedNeeded);
        }

        if (activeButNotNeeded.length) {
            this.command$.next({
                operation: subjectType.operations.UNSUBSCRIBE,
                requestId: subjectType.requestId,
                subject:   subjectType.isMultiSubject ? activeButNotNeeded : undefined
            });

            actualityMap.active = _.difference(actualityMap.active, activeButNotNeeded);
        }

        const subjectToDeprecate = _.union(outdatedNeeded, activeButNotNeeded);

        if (subjectToDeprecate.length && subjectType.deprecateSubject) {
            subjectType.deprecateSubject(subjectToDeprecate);
        }
    }

    private createStream(): Observable<T> {
        const messagePipe = this.subjectType.messagePipe;
        const message$ = messagePipe
            ? this.message$.pipe(messagePipe)
            : this.message$;

        return message$.pipe(share());
    }

    private createSubjectStream(subject: SubscriptionSubject): Observable<T> {
        const subjectPipeFactory = this.subjectType.subjectPipeFactory;
        const subjectPipe = subjectPipeFactory && subjectPipeFactory(subject);

        return subjectPipe
            ? this.stream$.pipe(subjectPipe)
            : this.stream$;
    }

    private deactivateSubjects(): void {
        const actualityMap: SubscriptionActualityMap = this.actualityMap;

        if (actualityMap.active) {
            actualityMap.needed = _.union(actualityMap.needed, actualityMap.active);
        }

        delete actualityMap.active;
    }

    private addSubject(subject: SubscriptionSubject): void {
        this.subjects.push(subject);

        this.innerActualize$.next();
    }

    private removeSubject(subject: SubscriptionSubject): void {
        _.pull(this.subjects, subject);

        this.innerActualize$.next();
    }

}
